import { gql, useQuery } from "@apollo/client";
import { Paper, Typography } from "@mui/material";
import ReadOnlyDetailField from "@src/components/fields/ReadOnlyDetail";
import { type GQLLogsByGroupIdentifierAdminQuery, type GQLLogsByGroupIdentifierAdminQueryVariables } from "@src/graphql.generated";
import { type FC, type PropsWithChildren } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";

import { generateContent, generateType } from "./activityLog";

const ContentWrapper = styled(Paper)`
    && {
        padding: ${({ theme }) => theme.spacing(6)};
        min-width: 400px;
    }
`;

interface Props {
    groupIdentifier: string;
}

const logsQuery = gql`
    query LogsByGroupIdentifierAdmin($groupIdentifier: String!) {
        logsByGroupIdentifierAdmin(groupIdentifier: $groupIdentifier) {
            id
            send
            receive
            createdAt
            message
            executedBy
        }
    }
`;

const ActivityInformationDetails: FC<PropsWithChildren<Props>> = ({ groupIdentifier }) => {
    const { data } = useQuery<GQLLogsByGroupIdentifierAdminQuery, GQLLogsByGroupIdentifierAdminQueryVariables>(logsQuery, {
        variables: { groupIdentifier },
    });
    const intl = useIntl();

    const send = data?.logsByGroupIdentifierAdmin.find((log) => ["SendSMS", "StartedMoveSim"].includes(log.message));
    const received = data?.logsByGroupIdentifierAdmin.find((log) => !["SendSMS", "StartedMoveSim"].includes(log.message));

    return (
        <ContentWrapper>
            {send && (
                <>
                    <Typography variant="h5" color="primary" gutterBottom>
                        <FormattedMessage id="reviermanager.logs.details.send" defaultMessage="Ausgehend" />
                    </Typography>

                    <ReadOnlyDetailField
                        type="date"
                        value={send.createdAt}
                        label={intl.formatMessage({ id: "reviermanager.logs.detail.createdAt", defaultMessage: "Datum" })}
                    />
                    <ReadOnlyDetailField
                        value={generateType(send.message, intl)}
                        label={intl.formatMessage({ id: "reviermanager.logs.detail.type", defaultMessage: "Typ" })}
                    />
                    <ReadOnlyDetailField
                        value={generateContent(intl, send)}
                        label={intl.formatMessage({ id: "reviermanager.logs.detail.content", defaultMessage: "Inhalt" })}
                    />
                    <ReadOnlyDetailField
                        value={send.send?.command}
                        label={intl.formatMessage({ id: "reviermanager.logs.detail.command", defaultMessage: "Befehl" })}
                    />
                    <ReadOnlyDetailField
                        value={send.executedBy || intl.formatMessage({ id: "reviermanager.camera.activity.user.camera", defaultMessage: "Kamera" })}
                        label={intl.formatMessage({ id: "reviermanager.logs.detail.user", defaultMessage: "Absender" })}
                    />
                </>
            )}
            {received && (
                <>
                    <Typography variant="h5" color="primary" gutterBottom>
                        <FormattedMessage id="reviermanager.logs.details.received" defaultMessage="Eingehend" />
                    </Typography>
                    <ReadOnlyDetailField
                        type="date"
                        value={received.createdAt}
                        label={intl.formatMessage({ id: "reviermanager.logs.detail.createdAt", defaultMessage: "Datum" })}
                    />
                    <ReadOnlyDetailField
                        value={generateType(received.message, intl)}
                        label={intl.formatMessage({ id: "reviermanager.logs.detail.type", defaultMessage: "Typ" })}
                    />
                    <ReadOnlyDetailField
                        value={generateContent(intl, received)}
                        label={intl.formatMessage({ id: "reviermanager.logs.detail.content", defaultMessage: "Inhalt" })}
                    />
                </>
            )}
        </ContentWrapper>
    );
};

export default ActivityInformationDetails;
